import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { 
  ArrowLeft, 
  MapPin, 
  Phone, 
  MessageCircle, 
  Copy, 
  CheckCircle, 
  AlertCircle,
  RefreshCw,
  Search,
  Filter,
  User,
  Package,
  Clock
} from 'lucide-react';

function CoordsModal({ open, onClose, onSave, deliveryPoint }) {
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  if (!open) return null;

  function handleSave() {
    const match = input.match(/^\s*(-?\d+(\.\d+)?)\s*,\s*(-?\d+(\.\d+)?)\s*$/);
    if (!match) {
      alert('Formato inválido. Usa: -12.00156295, -77.05527454');
      return;
    }
    setIsLoading(true);
    onSave(parseFloat(match[1]), parseFloat(match[3]));
    setInput('');
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full animate-in fade-in zoom-in duration-200">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-xl font-bold text-brand-primary font-montserrat">
            Ingresar Coordenadas Manualmente
          </h2>
          {deliveryPoint && (
            <p className="text-sm text-slate-600 font-montserrat mt-2">
              {deliveryPoint.recipient_name} - #{deliveryPoint.id}
            </p>
          )}
        </div>
        
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 font-montserrat mb-2">
              Coordenadas (Latitud, Longitud)
            </label>
            <input
              type="text"
              autoFocus
              placeholder="-12.00156295, -77.05527454"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent font-montserrat"
            />
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-xs text-blue-800 font-montserrat">
              <strong>Cómo obtener coordenadas:</strong><br/>
              1. Abre Google Maps<br/>
              2. Haz clic derecho en la ubicación exacta<br/>
              3. Copia las coordenadas (primer valor)<br/>
              4. Pégalas aquí
            </p>
          </div>
        </div>
        
        <div className="flex items-center justify-end gap-3 p-6 border-t border-slate-200">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-6 py-2.5 border border-slate-300 text-slate-700 font-semibold rounded-lg hover:bg-slate-50 transition-colors font-montserrat disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading}
            className="px-6 py-2.5 bg-gradient-to-r from-brand-primary to-brand-gold text-white font-semibold rounded-lg hover:shadow-lg transition-all duration-200 font-montserrat disabled:opacity-50 flex items-center gap-2"
          >
            {isLoading ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Guardando...
              </>
            ) : (
              'Guardar'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function Location({ setActiveComponent, userId }) {
  const [deliveryPoints, setDeliveryPoints] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStore, setFilterStore] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const fetchDeliveryPoints = useCallback(async () => {
    setIsLoading(true);
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/delivery_points/provider/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Filter only items without coordinates
      const missingLocations = response.data.store_deliveries.filter(
        dp => !dp.latitude || !dp.longitude
      );
      
      setDeliveryPoints(missingLocations);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error fetching delivery points:', error);
      alert('Error al cargar los puntos de entrega');
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchDeliveryPoints();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchDeliveryPoints, 30000);
    return () => clearInterval(interval);
  }, [fetchDeliveryPoints]);

  async function handleSaveCoords(lat, lng) {
    const token = localStorage.getItem('token');
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/delivery_point/update_coords`,
        {
          id: selectedDelivery.id,
          latitude: lat,
          longitude: lng,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Remove from list immediately
      setDeliveryPoints(prev => prev.filter(dp => dp.id !== selectedDelivery.id));
      setModalOpen(false);
      setSelectedDelivery(null);
    } catch (err) {
      console.error('Error al guardar coordenadas:', err);
      alert('Error al guardar las coordenadas.');
    }
  }

  function truncateText(text, maxLength) {
    if (!text) return '—';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  }

  function handleWhatsappClient(phone, recipientName) {
    const message = encodeURIComponent(
      `Hola ${recipientName}, necesitamos tu ubicación exacta para realizar la entrega. Por favor envíanos tu ubicación por WhatsApp lo antes posible. ¡Gracias! 📍`
    );
    window.open(`https://wa.me/+51${phone}?text=${message}`, '_blank');
  }

  function handleWhatsappStore(phone, recipientName) {
    const message = encodeURIComponent(
      `El cliente ${recipientName} aún no ha enviado la ubicación para la entrega. Por favor contáctelo para obtener la ubicación lo antes posible.`
    );
    window.open(`https://wa.me/+51${phone}?text=${message}`, '_blank');
  }

  function handleCopyLink(deliveryId) {
    const link = `https://www.fastmaxlima.pe/#/entrega/${deliveryId}`;
    navigator.clipboard.writeText(link);
    setCopiedId(deliveryId);
    setTimeout(() => setCopiedId(null), 2000);
  }

  function handleCopyLinkWithMessage(phone, deliveryId, recipientName) {
    const link = `https://www.fastmaxlima.pe/#/entrega/${deliveryId}`;
    const message = encodeURIComponent(
      `Hola ${recipientName}, para completar tu entrega necesitamos tu ubicación exacta. Por favor ingresa aquí: ${link} 📍`
    );
    window.open(`https://wa.me/+51${phone}?text=${message}`, '_blank');
  }

  // Filter logic
  const filteredDeliveryPoints = deliveryPoints.filter(dp => {
    const matchesSearch = 
      dp.recipient_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      dp.recipient_phone?.includes(searchTerm) ||
      dp.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(dp.id).includes(searchTerm);
    
    const matchesStore = filterStore === 'all' || dp.user_name === filterStore;
    
    return matchesSearch && matchesStore;
  });

  // Get unique store names
  const uniqueStores = [...new Set(deliveryPoints.map(dp => dp.user_name))].filter(Boolean);

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6">
        <button
          onClick={() => setActiveComponent('OptionsAdmin')}
          className="flex items-center gap-2 text-slate-600 hover:text-brand-primary transition-colors font-montserrat mb-4"
        >
          <ArrowLeft className="w-5 h-5" />
          Volver
        </button>
        
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-2xl font-bold text-brand-primary font-montserrat flex items-center gap-2">
                <MapPin className="w-6 h-6" />
                Ubicaciones Faltantes
              </h1>
              <p className="text-sm text-slate-600 font-montserrat mt-2">
                Gestiona las entregas que necesitan confirmación de ubicación
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="text-right">
                <div className="text-2xl font-bold text-brand-primary font-montserrat">
                  {filteredDeliveryPoints.length}
                </div>
                <div className="text-xs text-slate-500 font-montserrat">
                  Pendientes
                </div>
              </div>
              
              <button
                onClick={fetchDeliveryPoints}
                disabled={isLoading}
                className="p-3 bg-brand-primary text-white rounded-lg hover:bg-brand-primary/90 transition-colors disabled:opacity-50"
                title="Actualizar lista"
              >
                <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
          
          <div className="mt-4 text-xs text-slate-500 font-montserrat">
            Última actualización: {lastRefresh.toLocaleTimeString('es-PE')}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Buscar por nombre, teléfono, dirección, ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent font-montserrat text-sm"
              />
            </div>
            
            {/* Store Filter */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <select
                value={filterStore}
                onChange={(e) => setFilterStore(e.target.value)}
                className="pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent font-montserrat text-sm appearance-none bg-white cursor-pointer"
              >
                <option value="all">Todas las tiendas</option>
                {uniqueStores.map(store => (
                  <option key={store} value={store}>{store}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          {isLoading ? (
            <div className="p-12 text-center">
              <RefreshCw className="w-8 h-8 text-brand-primary mx-auto mb-3 animate-spin" />
              <p className="text-sm text-slate-600 font-montserrat">Cargando ubicaciones...</p>
            </div>
          ) : filteredDeliveryPoints.length === 0 ? (
            <div className="p-12 text-center">
              <CheckCircle className="w-12 h-12 text-brand-green mx-auto mb-3" />
              <p className="text-sm text-slate-600 font-montserrat">
                {searchTerm || filterStore !== 'all' 
                  ? 'No se encontraron resultados'
                  : '¡Excelente! No hay ubicaciones pendientes'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 font-montserrat whitespace-nowrap">
                      ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 font-montserrat whitespace-nowrap">
                      Cliente
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 font-montserrat whitespace-nowrap">
                      Contacto
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 font-montserrat whitespace-nowrap">
                      Dirección
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 font-montserrat whitespace-nowrap">
                      Producto
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 font-montserrat whitespace-nowrap">
                      Tienda
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 font-montserrat whitespace-nowrap">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredDeliveryPoints.map((dp) => (
                    <tr key={dp.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3 text-sm font-medium text-slate-900 font-montserrat whitespace-nowrap">
                        #{dp.id}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-slate-400" />
                          <div>
                            <div className="text-sm font-semibold text-slate-900 font-montserrat">
                              {truncateText(dp.recipient_name, 15)}
                            </div>
                            {dp.notes && (
                              <div className="text-xs text-slate-500 font-montserrat truncate max-w-xs" title={dp.notes}>
                                {truncateText(dp.notes, 15)}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4 text-slate-400" />
                          <span className="text-sm text-slate-700 font-montserrat">
                            {dp.recipient_phone}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-slate-700 font-montserrat max-w-xs truncate" title={dp.address}>
                          {truncateText(dp.address, 16)}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Package className="w-4 h-4 text-slate-400" />
                          <span className="text-sm text-slate-700 font-montserrat max-w-xs truncate" title={dp.product}>
                            {truncateText(dp.product, 12)}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-slate-700 font-montserrat">
                          {dp.user_name || '—'}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {/* WhatsApp to Client */}
                          <button
                            onClick={() => handleWhatsappClient(dp.recipient_phone, dp.recipient_name)}
                            className="p-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors"
                            title="Enviar mensaje al cliente"
                          >
                            <MessageCircle className="w-4 h-4" />
                          </button>
                          
                          {/* WhatsApp with Link */}
                          <button
                            onClick={() => handleCopyLinkWithMessage(dp.recipient_phone, dp.id, dp.recipient_name)}
                            className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                            title="Enviar link por WhatsApp"
                          >
                            <MessageCircle className="w-4 h-4" />
                          </button>
                          
                          {/* Manual Coords */}
                          <button
                            onClick={() => {
                              setSelectedDelivery(dp);
                              setModalOpen(true);
                            }}
                            className="p-2 bg-brand-primary/10 text-brand-primary rounded-lg hover:bg-brand-primary/20 transition-colors"
                            title="Ingresar coordenadas manualmente"
                          >
                            <MapPin className="w-4 h-4" />
                          </button>
                          
                          {/* Copy Link */}
                          <button
                            onClick={() => handleCopyLink(dp.id)}
                            className={`p-2 rounded-lg transition-colors ${
                              copiedId === dp.id
                                ? 'bg-brand-green/10 text-brand-green'
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                            title="Copiar enlace"
                          >
                            {copiedId === dp.id ? (
                              <CheckCircle className="w-4 h-4" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </button>
                          
                          {/* WhatsApp to Store */}
                          {dp.user_phone && (
                            <button
                              onClick={() => handleWhatsappStore(dp.user_phone, dp.recipient_name)}
                              className="p-2 bg-amber-50 text-amber-600 rounded-lg hover:bg-amber-100 transition-colors"
                              title="Notificar a la tienda"
                            >
                              <AlertCircle className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      <CoordsModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedDelivery(null);
        }}
        onSave={handleSaveCoords}
        deliveryPoint={selectedDelivery}
      />
    </div>
  );
}

export default Location;